import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "tab-version" }
const _hoisted_2 = { class: "tab-header" }
const _hoisted_3 = { class: "tab-content" }
const _hoisted_4 = {
  key: 0,
  class: "empty-data"
}
const _hoisted_5 = { class: "target-add" }
const _hoisted_6 = { class: "target-search" }
const _hoisted_7 = { class: "icon" }
const _hoisted_8 = { class: "name" }
const _hoisted_9 = ["for"]
const _hoisted_10 = ["id", "onChange", "checked"]

import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import store from "@/store";
import trim from "lodash-es/trim";

import { toast } from "@/common/utils/toast";
import { getDate, getHour } from "@/common/utils/dates";

import {
  versionRelatedList,
  versionRelatedListId,
  versionRelatedCreate,
  versionRelatedDelete,
} from "@/common/api/rest/common/version";

import { gameList } from "@/common/api/rest/game/game";
import { accessoryList } from "@/common/api/rest/accessory/accessory";
import { consoleList } from "@/common/api/rest/console/console";

import type {
  VersionOwnerType,
  VersionRelatedType,
  VersionRelationType,
  VersionTargetType,
} from "@/common/types/common/version";

import {
  baseAvatar,
  baseConfirm,
  baseLoading,
  baseInput,
  baseModal,
  baseSearchExact,
  baseTable,
  svgIcon,
} from "@/common/components";


export default /*@__PURE__*/_defineComponent({
  __name: 'version',
  props: {
    ownerType: {},
    ownerId: {},
    relationType: {}
  },
  setup(__props: any) {

const { t } = useI18n();

const props = __props;

const related = ref<VersionRelatedType[]>([]);
const relatedIds = ref<
  { item_id: string; target_type: VersionTargetType; target_id: string }[]
>([]);
const selected = ref<VersionRelatedType | null>(null);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const loading = ref(true);
const showModalAddRelated = ref(false);
const showModalRemoveRelated = ref(false);

const targetSearchListRef = ref<any>(null);
const targetSearch = ref<string>("");
const targetExactSearch = ref<boolean>(false);
const targetSearchList = ref<any[]>([]);
const targetLimit = ref<number>(10);
const targetPage = ref<number>(0);
const targetPages = ref<number>(0);
const targetSearched = ref<number>(0);
const targetTotal = ref<number>(0);
const loadingMoreTargets = ref(false);

const targetType = computed<VersionTargetType>(() =>
  props.relationType === "console_memory" ? "console" : props.ownerType,
);

const sectionLabel = computed(() => {
  const labels: Record<VersionRelationType, string> = {
    this_console: t("collection.labels.version_this_console"),
    other_systems: t("collection.labels.version_other_systems"),
    compilation: t("collection.labels.version_compilation"),
    console_memory: t("collection.labels.version_console_memory"),
    unofficial_other_systems: t(
      "collection.labels.version_unofficial_other_systems",
    ),
  };

  return labels[props.relationType];
});

const tableHeader = computed(() => [
  { key: "avatar", label: "" },
  { key: "name", label: t("common.labels.name") },
  { key: "created", label: t("common.labels.created") },
]);

function targetId(item: any): string {
  if (targetType.value === "console") {
    return item.console_id;
  }

  if (targetType.value === "accessory") {
    return item.accessory_id;
  }

  return item.game_id;
}

async function fetchRelated() {
  await versionRelatedList(props.ownerType, props.ownerId, {
    page: page.value,
    limit: limit.value,
    search: search.value,
    sort: "DESC",
    relation_type: props.relationType,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        related.value = data.list;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function fetchRelatedId() {
  await versionRelatedListId(
    props.ownerType,
    props.ownerId,
    props.relationType,
  )
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        relatedIds.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function searchTargets(append = false) {
  if (
    append &&
    (loadingMoreTargets.value || targetPage.value + 1 >= targetPages.value)
  ) {
    return;
  }

  if (append) {
    loadingMoreTargets.value = true;
  }

  const pageToFetch = append ? targetPage.value + 1 : 0;
  const query = {
    page: pageToFetch,
    limit: targetLimit.value,
    search: targetSearch.value,
    match: targetExactSearch.value ? ("exact" as const) : ("like" as const),
    sort: "ASC" as const,
  };

  const request =
    targetType.value === "console"
      ? consoleList(query)
      : targetType.value === "accessory"
        ? accessoryList(query)
        : gameList(query);

  await request
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        const newItems = (data.list ?? []).filter(
          (item: any) =>
            targetId(item) !== props.ownerId ||
            targetType.value !== props.ownerType,
        );
        targetSearchList.value = append
          ? [...targetSearchList.value, ...newItems]
          : newItems;
        targetPage.value = pageToFetch;
        targetPages.value = data.infos.pages;
        targetTotal.value = data.infos.total;
        targetSearched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      loadingMoreTargets.value = false;
    });
}

function changePage(value: number) {
  page.value = value;
  fetchRelated();
}

function changeLimit(value: number) {
  limit.value = value;
  page.value = 0;
  fetchRelated();
}

function searchItem(value: string) {
  search.value = value;

  fetchRelated();
}

function resetSearchTarget() {
  targetSearch.value = "";
  targetExactSearch.value = false;
  targetSearchList.value = [];
  targetPage.value = 0;
  targetPages.value = 0;
  targetSearched.value = 0;
  targetTotal.value = 0;
  loadingMoreTargets.value = false;
}

async function addRelated() {
  store.commit("SET_LOADING", true);

  resetSearchTarget();
  await searchTargets();
  store.commit("SET_LOADING", false);

  showModalAddRelated.value = true;
}

async function searchTarget() {
  targetSearchList.value = [];
  targetPage.value = 0;
  targetPages.value = 0;
  targetSearched.value = 0;
  targetTotal.value = 0;

  await searchTargets();
}

function onTargetListScroll(event: Event) {
  const target =
    (event.target as HTMLElement | null) ?? targetSearchListRef.value;

  if (!target) {
    return;
  }

  const reachedEnd =
    target.scrollTop + target.clientHeight >= target.scrollHeight - 24;

  if (
    reachedEnd &&
    !loadingMoreTargets.value &&
    targetPage.value + 1 < targetPages.value
  ) {
    void searchTargets(true);
  }
}

async function selectTarget(item: any) {
  const id = targetId(item);

  if (!relatedIds.value.some((r) => r.target_id === id)) {
    await versionRelatedCreate(props.ownerType, props.ownerId, {
      relation_type: props.relationType,
      target_type: targetType.value,
      target_id: id,
    })
      .then((response) => {
        const { success, message } = response;

        if (success) {
          fetchRelatedId();
          fetchRelated();
        } else {
          toast({ message: message, type: "error" });
        }
      })
      .catch(() => {
        toast({ message: t("error.default"), type: "error" });
      });
  } else {
    const relation = relatedIds.value.find((r) => r.target_id === id);

    if (!relation) {
      return;
    }

    await versionRelatedDelete(relation.item_id)
      .then((response) => {
        const { success, message } = response;

        if (success) {
          fetchRelatedId();
          fetchRelated();
        } else {
          toast({ message: message, type: "error" });
        }
      })
      .catch(() => {
        toast({ message: t("error.default"), type: "error" });
      });
  }
}

function removeRelated(item: VersionRelatedType) {
  selected.value = item;
  showModalRemoveRelated.value = true;
}

function removeRelatedCancel() {
  selected.value = null;
  showModalRemoveRelated.value = false;
}

async function removeRelatedConfirm() {
  if (!selected.value) {
    return;
  }

  await versionRelatedDelete(selected.value.item_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchRelatedId();
        fetchRelated();
        showModalRemoveRelated.value = false;
        selected.value = null;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

watch(
  () => targetSearch.value,
  () => {
    if (trim(targetSearch.value).length === 0) {
      searchTarget();
    }
  },
);

watch(
  () => targetExactSearch.value,
  () => {
    if (trim(targetSearch.value).length > 0) {
      searchTarget();
    }
  },
);

watch(
  () => props.ownerId,
  async () => {
    await fetchRelated();
    await fetchRelatedId();
  },
);

onMounted(async () => {
  await fetchRelated();
  await fetchRelatedId();
  loading.value = false;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("section", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(_unref(svgIcon), {
          icon: "layer",
          size: "2rem"
        }),
        _createTextVNode(" " + _toDisplayString(sectionLabel.value), 1)
      ]),
      _createElementVNode("div", _hoisted_3, [
        (loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createVNode(_unref(baseLoading))
            ]))
          : (_openBlock(), _createBlock(_unref(baseTable), {
              key: 1,
              header: tableHeader.value,
              data: related.value,
              limit: limit.value,
              page: page.value,
              pages: pages.value,
              total: total.value,
              searched: searched.value,
              options: true,
              copy: false,
              edit: false,
              del: true,
              "show-limit": true,
              "onChange:page": _cache[0] || (_cache[0] = ($event: any) => (changePage($event))),
              "onChange:limit": _cache[1] || (_cache[1] = ($event: any) => (changeLimit($event))),
              onSearching: _cache[2] || (_cache[2] = ($event: any) => (searchItem($event))),
              "onBtn:add": addRelated,
              "onBtn:del": _cache[3] || (_cache[3] = ($event: any) => (removeRelated($event)))
            }, {
              avatar: _withCtx(({ data }) => [
                _createVNode(_unref(baseAvatar), {
                  position: "center",
                  "is-edit": false,
                  "show-image": true,
                  url: data?.avatar?.large,
                  "aspect-ratio": 1.333,
                  size: "3rem",
                  "border-radius": "0.25rem"
                }, null, 8, ["url"])
              ]),
              created: _withCtx(({ data }) => [
                _createElementVNode("span", null, _toDisplayString(_unref(getDate)(data.created)) + " - " + _toDisplayString(_unref(getHour)(data.created)), 1)
              ]),
              _: 1
            }, 8, ["header", "data", "limit", "page", "pages", "total", "searched"]))
      ])
    ]),
    _createVNode(_unref(baseConfirm), {
      show: showModalRemoveRelated.value,
      title: _unref(t)('collection.modals.titles.del_version').toUpperCase(),
      message: 
      _unref(t)('collection.modals.messages.del_version', {
        name: selected.value?.name?.toUpperCase(),
      })
    ,
      onCancel: _cache[4] || (_cache[4] = ($event: any) => (removeRelatedCancel())),
      onConfirm: _cache[5] || (_cache[5] = ($event: any) => (removeRelatedConfirm()))
    }, null, 8, ["show", "title", "message"]),
    _createVNode(_unref(baseModal), {
      show: showModalAddRelated.value,
      title: _unref(t)('collection.modals.titles.add_version').toUpperCase(),
      onClose: _cache[10] || (_cache[10] = ($event: any) => (showModalAddRelated.value = false))
    }, {
      content: _withCtx(() => [
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createVNode(_unref(baseInput), {
              modelValue: targetSearch.value,
              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((targetSearch).value = $event)),
              background: "#fff",
              color: "#000",
              placeholder: _unref(t)('common.labels.search'),
              "onKey:enter": _cache[8] || (_cache[8] = ($event: any) => (searchTarget()))
            }, {
              sufix: _withCtx(() => [
                _createElementVNode("div", _hoisted_7, [
                  _createVNode(_unref(svgIcon), {
                    icon: "search",
                    size: "1.75rem",
                    onClick: _cache[6] || (_cache[6] = ($event: any) => (searchTarget()))
                  })
                ])
              ]),
              _: 1
            }, 8, ["modelValue", "placeholder"])
          ]),
          _createVNode(_unref(baseSearchExact), {
            modelValue: targetExactSearch.value,
            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((targetExactSearch).value = $event))
          }, null, 8, ["modelValue"]),
          _createElementVNode("div", {
            ref_key: "targetSearchListRef",
            ref: targetSearchListRef,
            class: "target-list",
            onScroll: onTargetListScroll
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(targetSearchList.value, (item, index) => {
              return (_openBlock(), _createElementBlock("div", {
                key: index,
                class: "target"
              }, [
                _createVNode(_unref(baseAvatar), {
                  position: "center",
                  "is-edit": false,
                  "show-image": true,
                  url: item?.avatar?.large,
                  "aspect-ratio": 1.333,
                  size: "3rem",
                  "border-radius": "0.25rem"
                }, null, 8, ["url"]),
                _createElementVNode("div", _hoisted_8, _toDisplayString(item.name), 1),
                _createElementVNode("label", {
                  class: "custom-checkbox",
                  for: `version-${__props.relationType}-${targetId(item)}`
                }, [
                  _createElementVNode("input", {
                    id: `version-${__props.relationType}-${targetId(item)}`,
                    class: "checkbox-input",
                    type: "checkbox",
                    onChange: ($event: any) => (selectTarget(item)),
                    checked: 
                    relatedIds.value.some((r) => r.target_id === targetId(item))
                  
                  }, null, 40, _hoisted_10),
                  _cache[11] || (_cache[11] = _createElementVNode("span", { class: "checkbox-mark" }, null, -1))
                ], 8, _hoisted_9)
              ]))
            }), 128))
          ], 544)
        ])
      ]),
      _: 1
    }, 8, ["show", "title"])
  ], 64))
}
}

})